'use client';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { supabase } from '@/lib/supabase';
import { useNotifications } from '@/contexts/NotificationContext';
import CustomerDashboardLayout from '../components/CustomerDashboardLayout';

interface Notification {
  id: string;
  title: string;
  body: string;
  notification_type: string;
  is_read: boolean;
  order_id: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

const TYPE_CONFIG: Record<string, { icon: string; cls: string; label: string }> = {
  order_status:       { icon: 'ShoppingBagIcon',          cls: 'bg-blue-100 text-blue-600',    label: 'Order Update' },
  payment:            { icon: 'CreditCardIcon',           cls: 'bg-green-100 text-green-600',  label: 'Payment' },
  promo:              { icon: 'TagIcon',                  cls: 'bg-purple-100 text-purple-600', label: 'Promotion' },
  system:             { icon: 'BellIcon',                 cls: 'bg-slate-100 text-slate-600',  label: 'System' },
  delivery_assigned:  { icon: 'TruckIcon',                cls: 'bg-indigo-100 text-indigo-600', label: 'Delivery' },
  delivery_completed: { icon: 'CheckCircleIcon',          cls: 'bg-green-100 text-green-600',  label: 'Delivered' },
  location_update:    { icon: 'MapPinIcon',               cls: 'bg-amber-100 text-amber-600',  label: 'Location' },
  driver_info:        { icon: 'UserCircleIcon',           cls: 'bg-cyan-100 text-cyan-600',    label: 'Driver' },
};

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

export default function CustomerNotificationsPage() {
  const router = useRouter();
  const { refreshUnread } = useNotifications();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [liveIndicator, setLiveIndicator] = useState(false);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  const fetchNotifications = useCallback(async (uid: string) => {
    const { data } = await supabase
      .from('notifications')
      .select('id, title, body, notification_type, is_read, order_id, metadata, created_at')
      .eq('user_id', uid)
      .order('created_at', { ascending: false })
      .limit(100);
    setNotifications(data || []);
  }, []);

  useEffect(() => {
    let mounted = true;

    async function init() {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { router.push('/customer-login'); return; }
      if (!mounted) return;

      await fetchNotifications(user.id);
      if (!mounted) return;
      setLoading(false);

      if (channelRef.current) {
        await supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }

      const channel = supabase
        .channel(`customer-notifications-${user.id}`)
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        }, (payload) => {
          if (!mounted) return;
          setNotifications(prev => [payload.new as Notification, ...prev]);
          setLiveIndicator(true);
          setTimeout(() => setLiveIndicator(false), 3000);
          refreshUnread();
        })
        .on('postgres_changes', {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        }, (payload) => {
          if (!mounted) return;
          const updated = payload.new as Notification;
          setNotifications(prev =>
            prev.map(n => n.id === updated.id ? { ...n, ...updated } : n)
          );
          refreshUnread();
        })
        .subscribe();

      channelRef.current = channel;
    }

    init();

    return () => {
      mounted = false;
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [router, fetchNotifications, refreshUnread]);

  const markAllRead = async () => {
    const unreadIds = notifications.filter(n => !n.is_read).map(n => n.id);
    if (unreadIds.length === 0) return;
    await supabase.from('notifications').update({ is_read: true }).in('id', unreadIds);
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    refreshUnread();
  };

  const markRead = async (id: string) => {
    await supabase.from('notifications').update({ is_read: true }).eq('id', id);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    refreshUnread();
  };

  const filtered = filter === 'unread' ? notifications.filter(n => !n.is_read) : notifications;
  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <CustomerDashboardLayout>
      <div className="space-y-6 max-w-3xl">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-700 text-slate-900">Notifications</h1>
              <span className={`flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border bg-green-50 border-green-200 text-green-600`}>
                <span className={`w-1.5 h-1.5 rounded-full bg-green-500 ${liveIndicator ? 'animate-ping' : ''}`} />
                Live
              </span>
            </div>
            <p className="text-sm text-slate-500 mt-0.5">
              {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'All caught up!'}
            </p>
          </div>
          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              className="text-sm text-primary font-600 hover:underline flex items-center gap-1.5"
            >
              <Icon name="CheckCircleIcon" size={16} />
              Mark all as read
            </button>
          )}
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2">
          {(['all', 'unread'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setFilter(tab)}
              className={`px-4 py-2 rounded-xl text-xs font-600 transition-colors ${
                filter === tab ? 'bg-primary text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-primary/40'
              }`}
            >
              {tab === 'all' ? `All (${notifications.length})` : `Unread${unreadCount > 0 ? ` (${unreadCount})` : ''}`}
            </button>
          ))}
        </div>

        {/* Notifications list */}
        <div className="space-y-2">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Icon name="ArrowPathIcon" size={28} className="animate-spin text-primary" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl py-16 text-center">
              <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <Icon name="BellSlashIcon" size={24} className="text-slate-300" />
              </div>
              <p className="text-sm text-slate-400">
                {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
              </p>
            </div>
          ) : (
            filtered.map(notif => {
              const cfg = TYPE_CONFIG[notif.notification_type] || TYPE_CONFIG.system;
              return (
                <div
                  key={notif.id}
                  onClick={() => !notif.is_read && markRead(notif.id)}
                  className={`bg-white border rounded-xl p-4 flex items-start gap-4 cursor-pointer hover:shadow-sm transition-all ${
                    !notif.is_read ? 'border-primary/30 bg-primary/5' : 'border-slate-200'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${cfg.cls}`}>
                    <Icon name={cfg.icon as never} size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className={`text-sm ${!notif.is_read ? 'font-700 text-slate-900' : 'font-500 text-slate-800'}`}>
                            {notif.title}
                          </p>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-500 ${cfg.cls}`}>
                            {cfg.label}
                          </span>
                          {!notif.is_read && (
                            <span className="w-2 h-2 bg-primary rounded-full flex-shrink-0" />
                          )}
                        </div>
                        {notif.body && (
                          <p className="text-xs text-slate-500 mt-0.5 leading-relaxed line-clamp-2">{notif.body}</p>
                        )}
                      </div>
                      <span className="text-xs text-slate-400 whitespace-nowrap flex-shrink-0">{timeAgo(notif.created_at)}</span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </CustomerDashboardLayout>
  );
}
